import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { LuShoppingCart } from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, aiServicePages, tech, workflowsteps, chooseus, localSeoFor, relatedServiceLinks } from "../controller/global";
import { submitLead } from "../controller/leads";

const service = companyServices.find((s) => s.key === "ecommerce_development");
const otherServices = companyServices.filter((s) => s.key !== "ecommerce_development");

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo }, { name: "BoxERP", src: Images.boxErp }, { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo }, { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo }, { name: "Stokkd", src: Images.stokkd_logo }, { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// This service has no named facets in controller/global.js, so this section
// is built directly from the service's own real description bullets.
const FEATURE_ROWS = [
  { num: "01 / PLATFORM", title: "Built on the right platform for your business.", copy: "We pick the platform to fit the business: Shopify for speed to market, WooCommerce for content-led stores, or a custom build when your pricing, catalog or B2B rules don't fit a template.", tags: ["Shopify", "WooCommerce", "Custom-built"] },
  { num: "02 / CHECKOUT", title: "Storefronts and checkout, built to convert.", copy: "Product pages, search, cart and checkout are built for speed and fewer steps, with payment options your customers expect, because most lost revenue in ecommerce is lost at checkout.", tags: ["Custom storefronts", "Payment gateways", "Checkout optimization"] },
  { num: "03 / GROWTH", title: "Analytics and tools that keep customers coming back.", copy: "Analytics, abandoned-cart emails, reviews and loyalty tools are wired in from launch, so you can see what sells and bring customers back instead of paying to acquire them again.", tags: ["Analytics", "Shipping integration", "Product management"] },
];

const PROCESS_STEPS = workflowsteps.map((w) => ({ n: w.number, t: w.title, d: w.description }));

const stackTools = tech.find((t) => t.key === "front-end")?.children.slice(0, 8).map((c) => c.name) ?? [];
const backEndTools = tech.find((t) => t.key === "back-end")?.children.map((c) => c.name) ?? [];

const FACTS = [
  { a: "MSME", b: "Registered IT company" }, { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" }, { a: "11", b: "Core services offered" },
];

const ENGAGEMENT_RESULTS = [
  { a: "Fixed-Scope", b: "Clearly defined deliverable" }, { a: "Dedicated Team", b: "Ongoing, evolving product work" },
  { a: "MVP Sprint", b: "Validate an idea fast" }, { a: "1", b: "Same process, every model" },
];

const FAQS = [
  { q: "Shopify, WooCommerce, or custom-built — which is right for us?", a: "It depends on your catalog size, customization needs, and budget — we'll help you weigh it during discovery rather than defaulting to one platform." },
  { q: "Do you offer support after our store launches?", a: "Yes — our Product Support & Maintenance service covers ongoing updates, bug fixes, and 24/7 assistance so your store keeps running smoothly after launch." },
  { q: "Shopify or custom ecommerce development: which is better?", a: "Shopify is faster and cheaper to launch for standard retail. A custom build makes sense for complex pricing, B2B ordering, marketplaces or deep integrations with your ERP." },
  { q: "How much does an ecommerce website cost in India?", a: "It depends on the platform, number of products and integrations. A theme-based Shopify store costs far less than a custom marketplace. We give a fixed estimate after a scoping call." },
  { q: "Can you connect the store to our inventory or ERP system?", a: "Yes. We integrate stock, orders and invoices with systems such as Tally, Zoho, SAP or a custom ERP so data isn't re-entered by hand." },
  { q: "What engagement models do you offer?", a: "We work Fixed-Scope, Dedicated Team, or MVP Sprint — the same process and team stay accountable from kickoff to launch regardless of which model fits your project." },
  { q: "Where is your team based?", a: "We're an MSME-registered IT company headquartered in Delhi NCR, following the same 7-stage delivery process across every engagement." },
];

const fadeUp = { hidden: { opacity: 0, y: 18 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };
const fadeUpRotate = { hidden: { opacity: 0, y: 18, rotate: 0 }, show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }) };

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>{children}</MotionTag>;
}

const EcommerceDevelopment = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const seo = localSeoFor(
      "/ecommerce-development",
      `${service.title} | InQmise Technologies`,
      service.description[0].slice(0, 160)
    );
    document.title = seo.title;
    const description = document.createElement("meta");
    description.name = "description"; description.content = seo.description;
    document.head.appendChild(description);
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `https://inqmisetechnologies.com${service.path}`);
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Service", "name": service.title, "description": service.description[0],
      "url": `https://inqmisetechnologies.com${service.path}`,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"], "serviceType": service.title,
    });
    document.head.appendChild(schemaScript);
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": FAQS.map((item) => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })) });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone": if (!value.trim()) return ""; return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email": if (!value.trim()) return "Email is required."; return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = { fullname: validateContactField("fullname", formData.fullname), phone: validateContactField("phone", formData.phone), email: validateContactField("email", formData.email), subject: validateContactField("subject", formData.subject), description: validateContactField("description", formData.description) };
    if (Object.values(newErrors).some(Boolean)) { setContactErrors(newErrors); return; }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => { setContactStatus("success"); setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" }); setContactErrors({}); })
      .catch(() => setContactStatus("error"));
  };
  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">E-commerce Development · built to convert</p>
          <h1>Ecommerce website development that turns visitors into <em>customers</em>.</h1>
          <p className="ea-lede">{service.description[1]}</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
            <a className="ea-text-link" href="#process">See our process</a>
          </div>
        </Reveal>
        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / E-COMMERCE <b>LIVE</b></div>
          <h2><LuShoppingCart aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />{service.description[3]}</h2>
          <div className="ea-query"><small>CHECKOUT</small>{service.description[2]}</div>
          <div className="ea-query"><small>GROWTH</small>{service.description[4]}</div>
          <div className="ea-flow"><b>Design</b><i>→</i><b>Build</b><i>→</i><b>Launch</b></div>
        </Reveal>
      </header>

      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track"><div className="ea-logo-track-inner">{[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />)}</div></div>
      </div>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">What's included</div><h2>Platform to<br />checkout, covered.</h2></div>
          <p>End-to-end e-commerce, from platform choice to post-purchase.</p>
        </Reveal>
        {FEATURE_ROWS.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div><h3>{item.title}</h3><p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      <section className="ea-services" id="process">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div><div className="ea-eyebrow">Our process</div><h2>Seven stages,<br />checkout tested.</h2></div>
            <p>The same disciplined process behind every service we offer.</p>
          </Reveal>
          <div className="aa-process-line">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
                <div className="aa-process-copy"><h3>{step.t}</h3><p>{step.d}</p></div>
                <div className="aa-process-node">{step.n}</div><div />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div><div className="ea-eyebrow">What we build with</div><h2>Real stack,<br />real numbers.</h2></div>
            <p>The same front-end stack shown on /technologies, and the facts behind the team.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Our storefront stack</h3>
              <div className="ea-tags" style={{ padding: "8px 0 0" }}>{stackTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}</div>
              <h3 style={{ marginTop: 20 }}>Our back-end stack</h3>
              <div className="ea-tags" style={{ padding: "8px 0 0" }}>{backEndTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}</div>
            </Reveal>
            <div className="ea-metrics">{FACTS.map((m, i) => (<Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}><strong>{m.a}</strong><span>{m.b}</span></Reveal>))}</div>
          </div>
        </div>
      </section>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">Engagement models</div><h2>Three ways to work<br />with us.</h2></div>
          <p>One accountable team, whichever model fits your project.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">FIXED-SCOPE / DEDICATED / MVP</div><h3>Same process, one accountable team.</h3>
            <p>Whether you need a clearly scoped deliverable, an ongoing dedicated team, or a fast MVP to validate an idea, the same process and the same team stay accountable from kickoff to launch.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss your project</button>
          </div>
          <div className="ea-results">{ENGAGEMENT_RESULTS.map((r) => (<div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>))}</div>
        </Reveal>
      </section>

      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div><div className="ea-eyebrow">Explore more</div><h2>Other services<br />we offer.</h2></div>
            <p>Every service follows the same disciplined process.</p>
          </Reveal>
          <div className="cs-tags-row" style={{ marginBottom: 32 }}>
            {(relatedServiceLinks["/ecommerce-development"] || []).map((s) => (<a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>))}
            {otherServices.map((s) => (<a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>))}
            <a className="cs-tag" href="/services" style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate("/services"); }}>All Services →</a>
          </div>
          <Reveal as="div" reduceMotion={reduceMotion} className="ea-section-head">
            <div><div className="ea-eyebrow">Want to add AI?</div><h2>Pair this with<br />an AI solution.</h2></div>
            <p>Every store can plug into our AI solutions when you're ready — recommendations, personalization, and more.</p>
          </Reveal>
          <div className="cs-tags-row">
            {aiServicePages.map((s) => (<a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>))}
            <a className="cs-tag" href="/ai-solutions" style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>All AI Solutions →</a>
          </div>
        </div>
      </section>

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div><div className="ea-eyebrow">Why choose us</div><h2>What sets our<br />team apart.</h2></div>
          <p>The same standards behind every service we deliver.</p>
        </Reveal>
        <div className="ea-cards">
          {chooseus.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-card" key={item.title}>
              <div className="ea-icon"><item.icon aria-hidden="true" /></div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head"><div className="ea-eyebrow">Common questions</div><h2>Ecommerce development FAQs</h2><p>Short answers to the questions we hear most.</p></Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i; const panelId = `ecom-faq-panel-${i}`; const buttonId = `ecom-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>{item.q}<span>{isOpen ? "−" : "+"}</span></button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ea-cta" aria-labelledby="ecom-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div><h2 id="ecom-contact-heading">Ready to launch your store?</h2>
            <p>Tell us what you're building and we'll help you scope the right approach.</p>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span><h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>
            <form onSubmit={handleContactSubmit} aria-label="E-commerce Development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && <p role="status" aria-live="polite" className="ea-form-msg success">Thanks — we've received your details and will be in touch shortly.</p>}
              {contactStatus === "error" && <p role="alert" aria-live="assertive" className="ea-form-msg error">Something went wrong submitting your request. Please try again.</p>}
              <div className="ea-field">
                <label htmlFor="ecom-fullname">Full Name</label>
                <input id="ecom-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "ecom-fullname-error" : undefined} />
                {contactErrors.fullname && <p id="ecom-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>
              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="ecom-phone">Phone Number (optional)</label>
                  <input id="ecom-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "ecom-phone-error" : undefined} />
                  {contactErrors.phone && <p id="ecom-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="ecom-email">Email</label>
                  <input id="ecom-email" name="email" type="email" value={formData.email} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "ecom-email-error" : undefined} />
                  {contactErrors.email && <p id="ecom-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>
              <div className="ea-field">
                <label htmlFor="ecom-subject">Subject</label>
                <input id="ecom-subject" name="subject" type="text" placeholder="e.g., A new online store" value={formData.subject} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "ecom-subject-error" : undefined} />
                {contactErrors.subject && <p id="ecom-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>
              <div className="ea-field">
                <label htmlFor="ecom-description">Project Details</label>
                <textarea id="ecom-description" name="description" rows={3} value={formData.description} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "ecom-description-error" : undefined} />
                {contactErrors.description && <p id="ecom-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>
              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">{contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default EcommerceDevelopment;
